import React, { useMemo } from 'react';
import { Row, Col, Skeleton } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { glassCardStyle } from '../../Performance/utils';

interface DashboardChartsProps {
  statsData: any;
  leadStatuses: any[];
  navigate: any;
  message: any;
  loading: boolean;
}

export const DashboardCharts: React.FC<DashboardChartsProps> = ({
  statsData,
  leadStatuses,
  navigate,
  message,
  loading,
}) => {
  const isCardLoading = loading || !statsData;

  const monthlyTrendOptions = useMemo(() => {
    if (!statsData?.monthly_stats) return null;
    return {
      chart: {
        type: 'area',
        height: 320,
        backgroundColor: 'transparent',
        style: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        },
      },
      title: {
        text: 'Tren Pertumbuhan Leads (Bulanan)',
        style: {
          color: '#1e293b',
          fontWeight: '700',
          fontSize: '15px',
        },
      },
      xAxis: {
        categories: statsData.monthly_stats?.map((item: any) => item.month) || [],
        labels: {
          style: { color: '#64748b' },
        },
      },
      yAxis: {
        title: {
          text: 'Jumlah Leads',
          style: { color: '#64748b' },
        },
        labels: {
          style: { color: '#64748b' },
        },
        gridLineColor: 'rgba(226, 232, 240, 0.6)',
      },
      tooltip: {
        headerFormat: '<b>Bulan {point.x}</b><br/>',
        pointFormat: '{series.name}: <b>{point.y} Leads</b>',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderColor: '#e2e8f0',
      },
      plotOptions: {
        area: {
          fillOpacity: 0.15,
          color: '#1890ff',
          lineColor: '#1890ff',
          lineWidth: 3,
          marker: {
            radius: 4,
            hover: {
              enabled: true,
              radius: 6,
            },
          },
        },
      },
      series: [
        {
          name: 'Leads Masuk',
          data: statsData.monthly_stats?.map((item: any) => item.count) || [],
        },
      ],
      credits: {
        enabled: false,
      },
    };
  }, [statsData]);

  const statusDistributionOptions = useMemo(() => {
    if (!statsData?.leads_by_status) return null;
    return {
      chart: {
        type: 'pie',
        height: 320,
        backgroundColor: 'transparent',
        style: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        },
      },
      title: {
        text: 'Segmentasi Leads Berdasarkan Tahapan',
        style: {
          color: '#1e293b',
          fontWeight: '700',
          fontSize: '15px',
        },
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.y} Leads ({point.percentage:.1f}%)</b>',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderColor: '#e2e8f0',
      },
      plotOptions: {
        pie: {
          innerSize: '60%',
          cursor: 'pointer',
          colors: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2', '#64748b'],
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b><br/>{point.percentage:.1f}%',
            distance: 15,
            style: {
              color: '#334155',
              fontSize: '11px',
              fontWeight: '600',
              textOutline: 'none',
            },
          },
          events: {
            click: (event: any) => {
              const statusName = event.point.name;
              const status = leadStatuses.find((s: any) => s.name === statusName);
              if (status) {
                message.info(`Menyaring data leads berdasarkan tahapan: ${statusName}`);
                navigate(`/leads/data?tahapanFU=${status.id}`);
              } else {
                message.warning(`Status "${statusName}" tidak ditemukan untuk filtering.`);
              }
            },
          },
        },
      },
      series: [
        {
          name: 'Jumlah',
          data:
            statsData.leads_by_status?.map((item: any) => ({
              name: item.status,
              y: item.count,
            })) || [],
        },
      ],
      credits: {
        enabled: false,
      },
    };
  }, [statsData, leadStatuses, navigate, message]);

  return (
    <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
      {/* Column Left: Monthly Trend */}
      <Col xs={24} lg={12}>
        <div style={glassCardStyle} className="glass-card-interactive">
          {isCardLoading ? (
            <Skeleton active paragraph={{ rows: 7 }} />
          ) : (
            monthlyTrendOptions && (
              <HighchartsReact highcharts={Highcharts} options={monthlyTrendOptions} />
            )
          )}
        </div>
      </Col>

      {/* Column Right: Status Distribution */}
      <Col xs={24} lg={12}>
        <div style={glassCardStyle} className="glass-card-interactive">
          {isCardLoading ? (
            <Skeleton active paragraph={{ rows: 7 }} />
          ) : (
            statusDistributionOptions && (
              <HighchartsReact highcharts={Highcharts} options={statusDistributionOptions} />
            )
          )}
        </div>
      </Col>
    </Row>
  );
};

export default DashboardCharts;
